var _excluded = ["accounts", "classes", "className", "didAccountChange", "isLoading", "isMinimal", "labels", "minimumSearchCharacters", "onExpandAccount", "onSearch", "selectedAccount", "setAccount", "SwitcherClasses"],
  _excluded2 = ["nestedAccounts", "nestedAccountCount"];
function _typeof(o) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) { return typeof o; } : function (o) { return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o; }, _typeof(o); }
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return _typeof(key) === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (_typeof(input) !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (_typeof(res) !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
import React, { useMemo, useCallback } from 'react';
import { arrayOf, bool, exact, func, number, object, oneOf, oneOfType, shape, string } from 'prop-types';
import clsx from 'clsx';
import { RadioButtonUnchecked, CheckCircleOutline, UnfoldMore } from 'mosaic-react-icons';
import { alpha, styled, useTheme } from '@mui/material/styles';
import cloneDeep from 'lodash.clonedeep';
import { fontSize } from "../sovos-navigation/constants/styles";
import SovosFixedWidthText from "../sovos-fixed-width-text";
import SovosTooltip from "../sovos-tooltip";
import SovosTypography from "../sovos-typography";
import Switcher from "../internals/components/switcher/Switcher";
import { collapsedWidthFactor } from "../themes/sovos-sizes";
import { spacingToNumber } from "../internals/utils";
import useMosaicTranslation from "../internals/i18n/useMosaicTranslation";
var findRecursively = function findRecursively(data, nestedProperty, comparisonFunction) {
  for (var i = 0; i < data.length; i += 1) {
    if (comparisonFunction(data[i])) {
      return data[i];
    }
    if (data[i][nestedProperty]) {
      var foundItem = findRecursively(data[i][nestedProperty], nestedProperty, comparisonFunction);
      if (foundItem) return foundItem;
    }
  }
  return undefined;
};
var hasValidId = function hasValidId(account) {
  if (!account) return false;
  if (!('id' in account)) return false;
  var id = account.id;
  if (typeof id === 'string') {
    if (id === '') return false;
    return true;
  }
  if (typeof id === 'number') return true;
  return false;
};
export var types = {
  PRODUCTION: 'production',
  TEST: 'test',
  DEFAULT: 'default'
};
var getAccountColors = function getAccountColors(palette) {
  return {
    production: palette.success.main,
    test: palette.grey.A200,
    default: 'transparent'
  };
};
var getIconStyles = function getIconStyles(spacing) {
  return {
    width: spacing(2),
    height: spacing(2),
    marginLeft: spacing(0.5),
    alignSelf: 'center'
  };
};
var fallbackAccounts = [{
  name: 'error'
}];
var StyledSwitcher = styled(Switcher)(function (_ref) {
  var isButtonClickable = _ref.isButtonClickable,
    selectedAccount = _ref.selectedItem,
    _ref$theme = _ref.theme,
    palette = _ref$theme.palette,
    themeShape = _ref$theme.shape,
    spacing = _ref$theme.spacing,
    typography = _ref$theme.typography,
    transitionDuration = _ref$theme.transitions.duration.short;
  var getBorderColor = function getBorderColor() {
    var accountColors = getAccountColors(palette);
    return selectedAccount ? accountColors[selectedAccount.type || 'default'] : accountColors.default;
  };
  return {
    '.SovosAccountSwitcher': {
      '&__buttonContents': _objectSpread(_objectSpread({}, typography.body1), {}, {
        alignItems: 'center',
        backgroundColor: alpha(palette.primary.light, 0.08),
        borderWidth: 2,
        borderStyle: 'solid',
        borderColor: getBorderColor(),
        borderRadius: themeShape.borderRadius,
        color: palette.primary.contrastText,
        cursor: isButtonClickable ? 'pointer' : 'default',
        display: 'flex',
        fontSize: fontSize,
        fontWeight: 400,
        height: spacing(5),
        justifyContent: undefined,
        padding: "0 ".concat(spacing(), " 0 ").concat(spacing(2.5)),
        transition: "background-color ".concat(transitionDuration, "ms"),
        width: '100%',
        '&:hover': {
          backgroundColor: alpha(palette.primary.light, 0.2)
        },
        '&--minimized': {
          justifyContent: 'center',
          padding: 0,
          marginRight: 0
        }
      }),
      '&__buttonContentsLabel': {
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'space-between',
        lineHeight: 1.2,
        textAlign: 'left',
        width: '100%',
        '& .icon': _objectSpread({}, getIconStyles(spacing))
      },
      '&__name': {
        flexGrow: '1'
      },
      '&__taxYear': {
        fontWeight: typography.fontWeightBold
      },
      '&__dropdownIndicator': {
        height: spacing(2.5),
        width: spacing(2.5)
      }
    }
  };
});
var StyledMenuItem = styled('div')(function (_ref2) {
  var spacing = _ref2.theme.spacing;
  return {
    display: 'flex',
    flexGrow: 1,
    maxWidth: '100%',
    '& .text': {
      paddingRight: spacing(1.5),
      flexGrow: 1
    },
    '& .icon': _objectSpread({}, getIconStyles(spacing))
  };
});
var generateId = function generateId(account) {
  return "".concat(account.name).concat(account.taxYear || '').concat(account.type || '');
};
var SovosAccountSwitcher = function SovosAccountSwitcher(_ref3) {
  var accountsProp = _ref3.accounts,
    classes = _ref3.classes,
    className = _ref3.className,
    didAccountChange = _ref3.didAccountChange,
    _ref3$isLoading = _ref3.isLoading,
    isLoading = _ref3$isLoading === void 0 ? false : _ref3$isLoading,
    _ref3$isMinimal = _ref3.isMinimal,
    isMinimal = _ref3$isMinimal === void 0 ? false : _ref3$isMinimal,
    labels = _ref3.labels,
    minimumSearchCharacters = _ref3.minimumSearchCharacters,
    onExpandAccount = _ref3.onExpandAccount,
    onSearch = _ref3.onSearch,
    selectedAccountProp = _ref3.selectedAccount,
    setAccount = _ref3.setAccount,
    SwitcherClasses = _ref3.SwitcherClasses,
    switcherProps = _objectWithoutProperties(_ref3, _excluded);
  var _useMosaicTranslation = useMosaicTranslation(),
    t = _useMosaicTranslation.t;
  var _useTheme = useTheme(),
    palette = _useTheme.palette,
    spacing = _useTheme.spacing;
  var accountColors = getAccountColors(palette);
  var externalSearch = !!onSearch;
  var areAccountsValid = Array.isArray(accountsProp) && (externalSearch || !!accountsProp.length && !!accountsProp[0].name);
  var accounts = areAccountsValid ? accountsProp : fallbackAccounts;
  var isButtonClickable = externalSearch || accounts.length > 1 || accounts[0].nestedAccounts;
  var selectedAccount = useMemo(function () {
    var shallowAccountComparison = function shallowAccountComparison(account) {
      return (hasValidId(account) ? account.id === selectedAccountProp.id : true) || account.name === selectedAccountProp.name && account.taxYear === selectedAccountProp.taxYear && account.type === selectedAccountProp.type;
    };
    var accountComparison = typeof didAccountChange === 'function' ? function (account) {
      return !didAccountChange(account, selectedAccountProp);
    } : shallowAccountComparison;
    var isInArray = !externalSearch && selectedAccountProp && !!findRecursively(accounts, 'nestedAccounts', accountComparison);
    var isValid = selectedAccountProp && selectedAccountProp.name && typeof selectedAccountProp.name === 'string';
    if (!isValid) {
      console.error('SovosAccountSwitcher received an invalid selectedAccount');
    }
    if (!externalSearch && !isInArray) {
      console.error('SovosAccountSwitcher did not find selectedAccount in accounts array');
    }
    var validAccount = externalSearch || isInArray || isValid && !areAccountsValid ? selectedAccountProp : accounts[0];
    if (hasValidId(validAccount)) return validAccount;
    return _objectSpread({
      id: generateId(validAccount)
    }, validAccount);
  }, [areAccountsValid, accounts, didAccountChange, externalSearch, selectedAccountProp]);
  var renderAccountIcon = useCallback(function (account, color) {
    var type = account.type;
    var Icon;
    var label;
    switch (type) {
      case types.TEST:
        Icon = RadioButtonUnchecked;
        label = (labels === null || labels === void 0 ? void 0 : labels.test) || t('accountSwitcher.test');
        break;
      case types.PRODUCTION:
        Icon = CheckCircleOutline;
        label = (labels === null || labels === void 0 ? void 0 : labels.production) || t('accountSwitcher.production');
        break;
      default:
        return null;
    }
    return /*#__PURE__*/React.createElement(SovosTooltip, {
      title: label
    }, /*#__PURE__*/React.createElement(Icon, {
      classes: {
        root: classes === null || classes === void 0 ? void 0 : classes.icon
      },
      className: "icon",
      style: {
        color: color
      }
    }));
  }, [classes, labels, t]);
  var accountData = useMemo(function () {
    var getItemContent = function getItemContent(account) {
      var name = account.name,
        taxYear = account.taxYear,
        type = account.type;
      return /*#__PURE__*/React.createElement(StyledMenuItem, {
        className: classes === null || classes === void 0 ? void 0 : classes.menuItem
      }, /*#__PURE__*/React.createElement(SovosFixedWidthText, {
        text: name,
        className: clsx('text', classes === null || classes === void 0 ? void 0 : classes.text)
      }), taxYear && /*#__PURE__*/React.createElement("span", null, taxYear), !!type && renderAccountIcon(account, accountColors[type]));
    };
    var getMenuItem = function getMenuItem(account) {
      var nestedAccounts = account.nestedAccounts,
        nestedAccountCount = account.nestedAccountCount,
        rest = _objectWithoutProperties(account, _excluded2);
      var item = _objectSpread({
        id: hasValidId(account) ? account.id : generateId(account),
        content: getItemContent(account),
        nestedItemCount: nestedAccountCount
      }, rest);

      // SovosTreeList has its own types and these would conflict
      if (item.type) {
        item.accountType = item.type;
        delete item.type;
      }
      if (nestedAccounts) {
        item.nestedItems = nestedAccounts.map(function (nestedAccount) {
          return getMenuItem(nestedAccount);
        });
      }
      return item;
    };
    return accounts.length ? cloneDeep(accounts).map(getMenuItem) : [];
  }, [classes, accountColors, accounts, renderAccountIcon]);
  var handleSetAccount = function handleSetAccount(item) {
    var accountMatcher = function accountMatcher(account) {
      return item.id === account.id;
    };
    var newSelectedAccount = findRecursively(accountData, 'nestedItems', accountMatcher);
    var result = _objectSpread({}, newSelectedAccount);
    if (result.accountType) {
      result.type = result.accountType;
    }
    delete result.accountType;
    delete result.content;
    delete result.nestedItems;
    setAccount(result);
  };
  var buttonContents = useMemo(function () {
    var showDropdown = !selectedAccount.taxYear && !selectedAccount.type && isButtonClickable;
    var renderTaxYear = function renderTaxYear() {
      return /*#__PURE__*/React.createElement(SovosTypography, {
        variant: isMinimal ? 'body2' : 'body1',
        className: "SovosAccountSwitcher__year"
      }, selectedAccount && selectedAccount.taxYear.substring(0, 2), /*#__PURE__*/React.createElement("span", {
        className: clsx('SovosAccountSwitcher__taxYear', classes === null || classes === void 0 ? void 0 : classes.taxYear)
      }, selectedAccount && selectedAccount.taxYear.substring(2)));
    };
    return isMinimal ? /*#__PURE__*/React.createElement("div", {
      className: clsx('SovosAccountSwitcher__buttonContents', 'SovosAccountSwitcher__buttonContents--minimized', classes === null || classes === void 0 ? void 0 : classes.buttonContentsMinimized, classes === null || classes === void 0 ? void 0 : classes.buttonContents),
      "data-testid": "SovosAccountSwitcher__buttonContents"
    }, selectedAccount.taxYear ? renderTaxYear() : /*#__PURE__*/React.createElement(UnfoldMore, {
      className: clsx('SovosAccountSwitcher__dropdownIndicator', classes === null || classes === void 0 ? void 0 : classes.dropdownIndicator),
      "data-testid": "SovosAccountSwitcher__dropdown-indicator"
    })) : /*#__PURE__*/React.createElement("div", {
      className: clsx('SovosAccountSwitcher__buttonContents', classes === null || classes === void 0 ? void 0 : classes.buttonContents),
      "data-testid": "SovosAccountSwitcher__buttonContents"
    }, /*#__PURE__*/React.createElement("div", {
      className: clsx('SovosAccountSwitcher__buttonContentsLabel', classes === null || classes === void 0 ? void 0 : classes.buttonLabel)
    }, /*#__PURE__*/React.createElement(SovosFixedWidthText, {
      text: selectedAccount.name,
      className: clsx('SovosAccountSwitcher__name', classes === null || classes === void 0 ? void 0 : classes.name)
    }), selectedAccount.taxYear && renderTaxYear(), renderAccountIcon(selectedAccount, selectedAccount.type && accountColors[selectedAccount.type].text), showDropdown && /*#__PURE__*/React.createElement(UnfoldMore, {
      className: clsx('SovosAccountSwitcher__dropdownIndicator', classes === null || classes === void 0 ? void 0 : classes.dropdownIndicator),
      "data-testid": "SovosAccountSwitcher__dropdown-indicator"
    })));
  }, [classes, accountColors, isButtonClickable, isMinimal, renderAccountIcon, selectedAccount]);
  var _ref4 = labels || {},
    loading = _ref4.loading,
    noResults = _ref4.noResults,
    searchTemplate = _ref4.searchTemplate;
  return /*#__PURE__*/React.createElement(StyledSwitcher, _extends({
    buttonContents: buttonContents,
    buttonTooltip: isMinimal ? selectedAccount.name : null,
    classes: SwitcherClasses,
    className: clsx('sovosAccountSwitcher', className),
    didSelectionChange: didAccountChange,
    items: accountData,
    isLoading: isLoading,
    labels: {
      loading: loading,
      noResults: noResults,
      searchTemplate: searchTemplate
    },
    minimumCharacters: minimumSearchCharacters,
    onExpandItem: onExpandAccount,
    onSearch: onSearch,
    onSelectItem: handleSetAccount,
    PopoverProps: {
      anchorOrigin: {
        horizontal: isMinimal ? spacingToNumber(spacing(collapsedWidthFactor)) : 0,
        vertical: isMinimal ? 'top' : 'bottom'
      }
    },
    selectedItem: selectedAccount,
    showSearch: true
  }, switcherProps));
};
var accountItemShape = {
  disabled: bool,
  id: oneOfType([number, string]),
  isLoading: bool,
  name: string.isRequired,
  nestedAccountCount: number,
  taxYear: string,
  type: oneOf(Object.values(types))
};
accountItemShape.nestedAccounts = arrayOf(shape(accountItemShape));
var accountItemPropTypes = shape(accountItemShape);
SovosAccountSwitcher.propTypes = {
  /**
   * Array of objects, each with
   *
   * - `id` string or number. Required if name + taxYear + type may not be unique
   * - `name` string
   * - 'taxYear` string
   * - `type` string, one of , 'production', or 'test', or undefined
   * - `disabled` bool
   * - `nestedAccounts` array of objects
   */
  accounts: arrayOf(accountItemPropTypes).isRequired,
  /**
   * Override or extend the styles applied to the component
   */
  classes: object,
  /**
   * Override or extend the styles applied to the component
   */
  className: string,
  /**
   * Custom account equality check
   */
  didAccountChange: func,
  /**
   * If `true`, `onSearch` is loading results
   */
  isLoading: bool,
  /**
   * If `true`, component is rendered in minimal state
   */
  isMinimal: bool,
  /**
   * Custom labels for account types and searching, should only be used for
   * internationalization
   */
  labels: exact({
    loading: string,
    noResults: string,
    production: string,
    searchTemplate: string,
    test: string
  }),
  /**
   * Minimum number of characters needed to trigger an asynchronous account search
   */
  minimumSearchCharacters: number,
  /**
   * Callback fired when a account is expanded. Used to asynchronously load
   * nested accounts `function(account: object) => void`
   */
  onExpandAccount: func,
  /**
   * Callback fired when text is entered into the search box. Used to
   * asynchronously search accounts. If `onSearch` is provided,
   * `selectedAccount` is required. `function(searchVal: string) => void`
   */
  onSearch: function onSearch(props) {
    var onSearch = props.onSearch,
      selectedAccount = props.selectedAccount;
    if (onSearch && typeof onSearch !== 'function') {
      return new Error('onSearch must be a function.');
    }
    if (onSearch && !selectedAccount) {
      return new Error('When onSearch is passed in, selectedAccount is required.');
    }
    return null;
  },
  /**
   * When missing, invalid, or not in the accounts array, the first account
   * will be selected.
   */
  selectedAccount: accountItemPropTypes,
  /**
   * Callback fired when a new account is selected
   */
  setAccount: func.isRequired,
  /**
   * Override or extend the styles applied to the Switcher component
   */
  SwitcherClasses: object
};
export default SovosAccountSwitcher;